import { useShallow } from 'zustand/react/shallow';
import { useBatchStore } from '@/hooks/data/batch/useBatchStore';
import { TaskStateBar } from '@/components/task-state-bar';
import { isDefined } from '@/utilities/guards';
import { TaskState } from '@/types/uploads';
import { useTranslations } from 'next-intl';

/**
 * Renders a single batch's bar. Subscribes to *its own* batch only, so a poll
 * tick on one batch re-renders only that bar — not every bar. This is safe
 * because `pollProgress` maps over the array, preserving the object reference of
 * unchanged batches, so this selector returns a referentially-equal value (no
 * re-render) when a sibling updates.
 *
 * No sub-tasks: TaskStateBar renders the row as non-expandable, so `open` can
 * never affect it — hardcoded false, matching ExportTaskBar.
 */
const BatchTaskBar = ({ batchId }: { batchId: string }) => {
  const batch = useBatchStore((state) => state.batches.find((item) => item.id === batchId));
  const removeBatch = useBatchStore((state) => state.removeBatch);
  const tUpload = useTranslations('upload');

  if (!isDefined(batch)) {
    return null;
  }

  return (
    <TaskStateBar
      open={false}
      taskId={batch.id}
      state={batch.state}
      progress={batch.progress}
      title={batch.label}
      subTitle={
        batch.state === TaskState.Errored
          ? batch.totalJobs > 0
            ? tUpload('batchFailed', { failed: batch.failedJobs || batch.totalJobs, total: batch.totalJobs })
            : tUpload('batchFailedGeneric')
          : undefined
      }
      onCancel={
        batch.state === TaskState.Finished || batch.state === TaskState.Errored
          ? () => removeBatch(batch.id)
          : undefined
      }
      onFinished={() => removeBatch(batch.id)}
      disabled
    />
  );
};

/**
 * Renders one bar per active batch, in the sidebar's shared Accordion.
 * Subscribes only to the list of batch *ids* (shallow-compared), so it
 * re-renders when batches are added/removed but not on progress ticks —
 * `pollProgress` calls `set()` once a second per running batch, and each
 * BatchTaskBar handles its own updates.
 */
export const BatchTaskBars = () => {
  const batchIds = useBatchStore(useShallow((state) => state.batches.map((batch) => batch.id)));

  return (
    <>
      {batchIds.map((batchId) => (
        <BatchTaskBar key={batchId} batchId={batchId} />
      ))}
    </>
  );
};
